<template>
    <div class="prescriptionContainer">
        <header class="header">
            <div class="logo">
                <div v-html="logoInline" class="logo-svg"></div>
            </div>
            <div class="info">
                <span class="doctor">{{ doctorName }}</span>
                <p class="specialties">{{ doctorQualifications }}</p>

                <p
                    v-for="(value, index) in doctorUniversity"
                    :key="'univ-' + index"
                    class="university">
                    {{ value }}
                </p>

                <p
                    v-for="(value, index) in doctorLicenses"
                    :key="index"
                    class="license">
                    Cédula{{
                        value.type !== 'Provisional' ? ' ' + value.type : ''
                    }}:
                    {{ value.identifier }}
                </p>
            </div>
            <div class="qr-container">
                <div class="qr-legend">Cupón</div>
                <div id="qr"></div>
                <div class="qr-legend">De$cuento</div>
            </div>
            <div class="separator decoration"></div>
            <section style="width: 100%">
                <div class="medication-request-info">
                    <div class="name-grid">
                        <span class="data-label">Nombre:</span>
                        <span class="data-value">{{ patientName }}</span>
                    </div>
                    <div class="weight-grid">
                        <span class="data-label">Peso:</span>
                        <span class="data-value">
                            {{ patientWeight }}
                            <span v-if="patientWeight">Kg</span>
                        </span>
                    </div>
                </div>

                <div class="medication-request-info">
                    <div class="date-grid-start">
                        <span class="data-label">Fecha de elaboración:</span>
                        <span class="data-value">{{ date }}</span>
                    </div>
                    <div class="date-grid-end">
                        <span class="data-label">Edad:</span>
                        <span class="data-value">{{ patientAge }}</span>
                    </div>
                </div>
            </section>
        </header>
        <div class="content">
            <div class="prescriptions">
                <template
                    v-if="
                        prescriptions[0]?.itemDescription !=
                            'Sin medicamentos' && prescriptions.length > 0
                    ">
                    <div
                        v-for="(prescription, index) in prescriptions"
                        :key="index"
                        class="medication-request-info medication-grid main-content"
                        :class="{ 'full-width': !isPrint }">
                        <div class="preview-limits trebuchet">
                            <div v-if="prescription.dosageInstruction">
                                <span
                                    v-html="formatDosageInstruction(prescription.dosageInstruction)"
                                    class="mr-1 trebuchet_font"></span>
                                <span
                                    v-if="prescription.productBoxQuantity"
                                    class="trebuchet_font">
                                    (
                                    {{ prescription.productBoxQuantity }}
                                    piezas).
                                </span>
                            </div>
                            <div v-else class="medication-content">
                                <b class="trebuchet_font">
                                    <span
                                        v-if="prescription.requireMarca && prescription.itemBrand"
                                        class="trebuchet_font">
                                        {{ prescription.itemBrand }},
                                    </span>
                                    {{ prescription.itemDescription }}
                                </b>
                                <span
                                    v-if="formatPatientInstruction(prescription)"
                                    class="trebuchet_font">
                                    , {{ formatPatientInstruction(prescription) }}
                                </span>
                                <span
                                    v-if="prescription.productBoxQuantity"
                                    class="trebuchet_font">
                                    (
                                    {{ prescription.productBoxQuantity }}
                                    piezas).
                                </span>
                            </div>
                        </div>
                        <div
                            v-if="!isPrint"
                            class="medication-delete decoration">
                            <v-btn
                                class="decoration"
                                icon="mdi-delete"
                                @click="deletePrescription(index)"></v-btn>
                            <v-btn
                                class="decoration ml-1"
                                icon="mdi mdi-pencil"
                                @click="editPrescription(index)"></v-btn>
                        </div>
                    </div>
                </template>
                <template v-else-if="!finalNote">
                    <div class="empty-prescriptions">
                        No hay medicamentos registrados en esta receta.
                    </div>
                </template>
            </div>
            <div class="note" v-if="finalNote">
                <b class="trebuchet_font"> Nota: </b>
                {{ formatFinalNote(finalNote) }}
            </div>
        </div>
        <footer class="print-footer">
            <div class="footer">
                <div class="content">
                    <div class="signature">
                        <div class="firma">{{ doctorName }}</div>
                    </div>
                </div>
                <div class="separator decoration"></div>
                <div>
                    <!-- no recibe zip code correctamente -->
                    <p class="footer-style" style="font-style: italic">
                        {{ [
                            doctorAddress?.address ? doctorAddress.address : '',
                            doctorAddress?.noext ? 'No. ' + doctorAddress.noext : '',
                            doctorAddress?.suburb ? doctorAddress.suburb : '',
                            (doctorZIPCode || doctorAddress?.postalCode) ? 'C.P. ' + (doctorZIPCode || doctorAddress.postalCode) : ''
                        ].filter(Boolean).join(', ') }}
                        <span v-if="doctorPhone" style="margin-left: 5px;">
                            <b>Contacto:</b> {{ doctorPhone }}
                        </span>
                    </p>
                    <p class="footer-style">{{ doctorInformation }}</p>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import logoSvg from '@/assets/logo_receta.svg';

let cachedLogoSvg = '';

// ==========================================
// CONSTANTES GLOBALES (Extraídas por rendimiento)
// Estos diccionarios se usan para traducir los datos crudos del
// medicamento (frecuencia, vía, etc.) a un texto amigable para el paciente.
// ==========================================
const UNIT_MAP = {
    'Año(s)': ['Año', 'Años'],
    'Mes(es)': ['Mes', 'Meses'],
    'Semana(s)': ['Semana', 'Semanas'],
    'Día(s)': ['Día', 'Días'],
    'Hora(s)': ['Hora', 'Horas'],
};

const ADMINISTRATION_FORM = {
    Oral: 'Tomar',
    Nasal: 'Aplicar',
    Inhalación: 'Aplicar',
    Oftálmica: 'Aplicar',
    Intramuscular: 'Inyectar',
    Ótica: 'Aplicar',
    Sublingual: 'Colocar',
    Tópica: 'Aplicar',
    Cutánea: 'Aplicar',
    Subcutánea: 'Aplicar',
    Transdérmica: 'Aplicar',
    Rectal: 'Insertar',
    Vaginal: 'Aplicar',
    Orodispersable: 'Tomar',
    nebulizacion: 'Inhalar',
};

const FRACTION_MAP = {
    0.25: 'un cuarto',
    0.5: 'medio',
    0.75: 'tres cuartos',
    1.25: 'una y un cuarto',
    1.5: 'una y media',
    1.75: 'una y tres cuartos',
    2.25: 'dos y un cuarto',
    2.5: 'dos y medio',
    2.75: 'dos y tres cuartos',
};

export default {
    name: 'RecetaContenedor',

    props: {
        doctorName: {
            type: String,
            default: '',
        },
        doctorSpecialties: {
            type: Array,
            default: () => [],
        },
        date: {
            type: String,
            default: '',
        },
        patientName: {
            type: String,
            default: '',
        },
        patientAge: {
            type: [String, Number],
            default: null,
        },
        patientWeight: {
            type: String,
            default: '',
        },
        prescriptions: {
            type: Array,
            default: () => [],
            required: false,
        },
        doctorInformation: {
            type: String,
            default: '',
        },
        doctorSignature: {
            type: String,
            default: '',
        },
        doctorZIPCode: {
            type: String,
            default: '',
        },
        doctorAddress: {
            type: Object,
            required: true,
        },
        doctorPhone: {
            type: String,
            default: '',
        },
        finalNote: {
            type: String,
            default: '',
        },
        isPrint: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['deletePrescription', 'editPrescription'],
    data() {
        return {
            logoInline: '',
        };
    },
    created() {
        if (cachedLogoSvg) {
            this.logoInline = cachedLogoSvg;
            return;
        }
        fetch(logoSvg)
            .then((res) => res.text())
            .then((svgText) => {
                cachedLogoSvg = svgText;
                this.logoInline = svgText;
            })
            .catch(() => {});
    },
    computed: {
        // Asegura que las especialidades siempre sean un array válido para evitar errores (programación defensiva).
        validSpecialties() {
            return Array.isArray(this.doctorSpecialties)
                ? this.doctorSpecialties
                : [];
        },
        // Filtra solo las especialidades 'Profesional' y devuelve las cédulas separadas por ' / '
        doctorQualifications() {
            return this.validSpecialties
                .filter((practitioner) => practitioner?.type === 'Profesional')
                .map((practitioner) => practitioner?.professionalLicense)
                .filter(Boolean)
                .join(' / ');
        },
        // Obtiene una lista única de las universidades (issuers) donde estudió el doctor
        doctorUniversity() {
            return [
                ...new Set(
                    this.validSpecialties
                        .map((practitioner) => practitioner?.issuer?.reference)
                        .filter(Boolean)
                ),
            ];
        },
        // Devuelve un arreglo de todas las licencias/cédulas con su identificador y su tipo
        doctorLicenses() {
            return this.validSpecialties.map((practitioner) => ({
                identifier: practitioner?.identifier,
                type: practitioner?.type,
            }));
        },
    },
    methods: {
        deletePrescription(index) {
            this.$emit('deletePrescription', index);
        },
        editPrescription(index) {
            this.$emit('editPrescription', index);
        },
        // Transforma los datos técnicos del medicamento (frecuencia, duración, vía)
        // en una instrucción legible. Ej: "Tomar una y media Tableta cada 8 Horas..."
        setPatientInstruction(packProduct) {
            if (!packProduct.productRoute || !packProduct.productPeriodTime) {
                return '';
            }

            // Define si se usa la forma singular o plural (Ej: 'Hora' vs 'Horas')
            const finalPeriodDuraton =
                packProduct.productPeriodTime == 1
                    ? packProduct.productPeriodTime +
                      ' ' +
                      UNIT_MAP[packProduct.productTypePeriodTime]?.[0]
                    : packProduct.productPeriodTime +
                      ' ' +
                      UNIT_MAP[packProduct.productTypePeriodTime]?.[1];

            const finalBoundsDuration =
                packProduct.productDurationTime == 1
                    ? packProduct.productDurationTime +
                      ' ' +
                      UNIT_MAP[packProduct.productTypeDurationTime]?.[0]
                    : packProduct.productDurationTime +
                      ' ' +
                      UNIT_MAP[packProduct.productTypeDurationTime]?.[1];

            return (
                (ADMINISTRATION_FORM[packProduct.productRoute] || 'Aplicar') +
                ' ' +
                this.formatFrequency(packProduct.productFrequency) +
                ' ' +
                packProduct.productDosageForm +
                ' cada ' +
                finalPeriodDuraton +
                ' durante ' +
                finalBoundsDuration +
                ', Vía ' +
                packProduct.productRoute
            );
        },
        // Convierte las frecuencias en decimales a texto fraccionario (Ej: 1.5 -> "una y media")
        formatFrequency(frequency) {
            const standardizedFrequency = parseFloat(frequency);
            return FRACTION_MAP[standardizedFrequency] || frequency;
        },
        // Normaliza de forma inteligente las instrucciones para evitar puntos duplicados ("..") y asegurar mayúscula
        formatDosageInstruction(text) {
            if (!text) return '';
            let cleaned = text.trim().replace(/\.{2,}/g, '.');
            cleaned = cleaned.replace(/[.,\s]+$/, '') + '.';
            // Si viene en formato "<b>Medicamento</b>, instruccion", asegurar que después de "</b>, " la primera letra sea mayúscula
            cleaned = cleaned.replace(/(<\/b>\s*,\s*)([a-záéíóúñ])/i, (match, prefix, char) => {
                return prefix + char.toUpperCase();
            });
            // Si empieza directamente con texto sin tags HTML, asegurar mayúscula inicial
            if (!cleaned.startsWith('<') && cleaned.length > 0) {
                cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
            }
            return cleaned;
        },
        // Formatea la instrucción completa en el preview garantizando exactamente un punto al final y mayúscula inicial
        formatPatientInstruction(packProduct) {
            const parts = [];
            const instruction = this.setPatientInstruction(packProduct);
            if (instruction) {
                parts.push(instruction.trim().replace(/[.,\s]+$/, ''));
            }
            if (packProduct.productNote) {
                let note = packProduct.productNote.trim().replace(/[.,\s]+$/, '');
                if (note) {
                    // Si no hay dosis previa (medicamento controlado), asegurar mayúscula inicial
                    if (parts.length === 0) {
                        note = note.charAt(0).toUpperCase() + note.slice(1);
                    }
                    parts.push(note);
                }
            }
            if (parts.length === 0) return '';
            let combined = parts.join(', ').trim().replace(/\.{2,}/g, '.');
            combined = combined.replace(/[.,\s]+$/, '') + '.';
            combined = combined.charAt(0).toUpperCase() + combined.slice(1);
            return combined;
        },
        // Formatea la nota final de la receta con mayúscula inicial y punto final único
        formatFinalNote(note) {
            if (!note) return '';
            let cleaned = note.trim().replace(/\.{2,}/g, '.');
            cleaned = cleaned.replace(/[.,\s]+$/, '') + '.';
            return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
        },
    },
};
</script>

<style scoped>
.prescriptionContainer {
    margin: auto;
    width: 100%;
    max-width: 140mm;
    display: flex;
    flex-direction: column;
    height: 204mm;
    max-height: 204mm;
    padding: 1px;
    box-sizing: border-box;
}

.header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 10px;
    width: 100%;
}

.logo {
    flex: 0.16;
    display: flex;
    justify-content: center;
}

.logo img {
    width: 100%;
}

.info {
    flex: 0.84;
    text-align: center;
}

.doctor {
    font-family: 'Amaranth';
    font-size: 15pt;
    color: #000;
}

.info p {
    font-family: 'Trebuchet MS';
    font-size: 11pt;
    color: #120101;
}

.qr-container {
    flex: 0.09;
    max-width: 115px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

#qr {
    display: block;
    aspect-ratio: 1 / 1;
    width: 71px;
    flex-shrink: 0;
    background: repeating-linear-gradient(
        45deg,
        #e0e0e0,
        #e0e0e0 5px,
        #f5f5f5 5px,
        #f5f5f5 10px
    );
}

#qr :deep(svg) {
    display: block;
    width: 100%;
    height: 100%;
}

.qr-legend {
    font-size: 8pt;
    font-family: 'Trebuchet MS';
    text-align: center;
    margin: 1px 0;
    color: #000;
    line-height: 1.1;
    white-space: nowrap;
}

.separator {
    border-bottom: 4pt solid #000000;
    margin: 3px 0 5px 0;
    width: 100%;
    position: relative;
}

.separator::before {
    position: absolute;
    content: '';
    display: block;
    border-bottom: 1pt solid #151515;
    margin-top: 8px;
    width: 100%;
}

.content {
    flex-grow: 1;
    overflow-y: auto;
}

.medication-request-info {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 10px;
}

.data-label,
.data-value {
    display: inline-block;
    width: auto;
    color: black;
}

.data-value {
    flex: 1;
    flex-grow: 1;
    border-bottom: 1px solid black;
    font-family: 'Trebuchet MS';
    font-size: 11pt;
    text-align: center;
}

.data-label {
    margin-right: 10px;
    font-family: 'Trebuchet MS';
    font-weight: bold;
    font-size: 11pt;
}

.name-grid {
    grid-column: 1 / 4;
    display: flex;
}

.weight-grid {
    grid-column: 4 / 5;
    display: flex;
}

.date-grid-start {
    grid-column: 1/3;
}

.date-grid-end {
    grid-column: 3/5;
}

.date-grid-end .data-value {
    width: 100%;
    margin-left: 10px;
}

.date-grid-end {
    display: flex;
    align-items: center;
}


.prescriptions {
    margin-top: 10px;
    flex: 1;
    overflow-y: auto;
}

.medication-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px;
    width: 100%;
    align-items: stretch;
}

.preview-limits {
    font-size: 10pt;
    font-family: 'Trebuchet MS';
    white-space: pre-wrap;
    overflow-wrap: break-word;
    width: 100%;
    margin-bottom: 20px;
}

.main-content {
    overflow: hidden;
    min-width: 0;
}

.main-content.full-width {
    grid-column: 1 / -1;
}

.medication-delete {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin: 3px;
}

.print-footer {
    margin-top: 3px;
}

.footer {
    flex-shrink: 0;
    margin-top: 10px;
}

.signature {
    text-align: center;
}

.signature .firma {
    border-top: 1pt solid black;
    width: 48%;
    margin: 60px auto 0;
    text-align: center;
    font-family: 'Amaranth';
    font-size: 10pt;
}

.footer-style {
    color: black;
    text-align: center;
    font-size: 8pt;
    font-family: 'Trebuchet MS';
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
}


.empty-prescriptions {
    text-align: center;
    font-family: 'Trebuchet MS';
    font-size: 11pt;
    color: #666;
    padding: 20px 0;
}

.trebuchet_font {
    font-family: 'Trebuchet MS';
    font-size: 11pt;
}

.trebuchet {
    font-family: 'Trebuchet MS';
}

.note {
    font-family: 'Trebuchet MS';
    font-size: 11pt;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    margin-bottom: 20px;
}

.logo-svg {
    fill: black !important;
    stroke: black !important;
    filter: none !important;
}
</style>
